import type { ReactNode } from 'react';

import { Collection, type Key } from 'react-aria-components/Collection';
import {
  Tree as RACTree,
  type Selection,
  TreeItem,
  TreeItemContent,
} from 'react-aria-components/Tree';
import type { DragAndDropHooks } from 'react-aria-components/useDragAndDrop';
import { ListLayout, Virtualizer } from 'react-aria-components/Virtualizer';

import { cx } from '../../utils/cx';
import { Button } from '../button/button';
import { Icon } from '../icon/icon';
import type { IconName } from '../icon/icon_names';
import { ProgressCircle } from '../progress_circle/progress_circle';
import { Stack } from '../stack/stack';
import { Text } from '../text/text';
import { Tooltip } from '../tooltip/tooltip';
import { TooltipTrigger } from '../tooltip_trigger/tooltip_trigger';

import styles from './tree.module.css';

export type TreeNode = {
  id: string;
  title: string;
  children?: TreeNode[];
  disabled?: boolean;
  hasChildItems?: boolean;
  loading?: boolean;
  page?: number;
  pages?: number;
  isLeafNode?: boolean; // True when expanded and found to have no children
  parentId?: string; // For load more items, the parent collection ID
  subtitle?: ReactNode;
  shouldHideToggle?: boolean;
  titleIcon?: IconName;
};

export type TreeProps = {
  items: TreeNode[];
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  defaultExpandedKeys?: Iterable<Key>;
  expandedKeys?: Iterable<Key>;
  onExpandedChange?: (keys: Set<Key>) => void;
  selectionMode?: 'single' | 'multiple' | 'none';
  selectionBehavior?: 'toggle' | 'replace';
  selectedKeys?: Iterable<Key>;
  onSelectionChange?: (keys: Selection) => void;
  onAction?: (key: Key) => void;
  onLoadMore?: (parentId: string) => void;
  onItemButtonPress?: (key: Key) => void;
  actionIcon?: IconName;
  disallowEmptySelection?: boolean;
  disabledBehavior?: 'all' | 'selection';
  dragAndDropHooks?: DragAndDropHooks;
  isVirtualized?: boolean;
  estimatedRowSize?: number;
};

const renderToggleButton = (item: TreeNode, isExpanded: boolean) => {
  if (item.shouldHideToggle) {
    return <div aria-hidden="true" />;
  }
  const button = (
    <Button
      slot="chevron"
      aria-label={isExpanded ? 'Collapse' : 'Expand'}
      className={cx([styles.toggle_icon, styles.chevron])}
      icon={<Icon name="chevron_small_isolated_e" size="s" />}
      variant="tertiary"
      size="s"
    />
  );
  if (item.isLeafNode) {
    return (
      <TooltipTrigger>
        {button}
        <Tooltip>No sub-collections</Tooltip>
      </TooltipTrigger>
    );
  } else {
    return button;
  }
};

export function Tree({
  items,
  className,
  defaultExpandedKeys,
  expandedKeys,
  onExpandedChange,
  selectionMode = 'single',
  selectionBehavior = 'toggle',
  selectedKeys,
  onSelectionChange,
  onAction,
  onLoadMore,
  onItemButtonPress,
  actionIcon = 'go_to',
  disallowEmptySelection,
  disabledBehavior,
  dragAndDropHooks,
  isVirtualized,
  estimatedRowSize = 36,
  ...aria
}: TreeProps) {
  // Convert selectedKeys to a Set for easy lookup
  let selectedKeysSet: Set<Key>;
  if (selectedKeys) {
    selectedKeysSet =
      selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys);
  } else {
    selectedKeysSet = new Set();
  }

  function renderItem(item: TreeNode, level = 1) {
    if (item.parentId && onLoadMore) {
      return (
        <TreeItem
          id={item.id}
          textValue="Load more"
          className={styles.item}
          style={{ '--level-indent': `${(level - 1) * 20}px` }}
        >
          <TreeItemContent>
            <div className={styles.load_more_container}>
              <Button
                variant="tertiary"
                size="s"
                onPress={() => {
                  if (item.parentId) {
                    onLoadMore(item.parentId);
                  }
                }}
              >
                Load more
              </Button>
            </div>
          </TreeItemContent>
        </TreeItem>
      );
    }

    // Add load more item to children if needed
    const childrenWithLoadMore = item.children ? [...item.children] : [];
    if (item.page && item.pages && item.page < item.pages && onLoadMore) {
      childrenWithLoadMore.push({
        id: `${item.id}__load_more`,
        title: 'Load more',
        parentId: item.id,
      });
    }

    return (
      <TreeItem
        id={item.id}
        textValue={item.title}
        className={styles.item}
        isDisabled={item.disabled}
        hasChildItems={item.hasChildItems ?? true}
        style={{ '--level-indent': `${(level - 1) * 20}px` }}
        data-selected={selectedKeysSet.has(item.id) ? true : undefined}
        data-selection-disabled={item.disabled ? true : undefined}
      >
        <TreeItemContent>
          {({ isExpanded }) => {
            return (
              <div
                className={styles.item_layout}
                data-expanded={isExpanded ? true : undefined}
              >
                {renderToggleButton(item, isExpanded)}
                <div className={styles.item_text_container}>
                  <Stack
                    direction="row"
                    gap="s"
                    alignItems="center"
                    className={styles.item_content}
                  >
                    {item.titleIcon && (
                      <Text color="medium_emphasis">
                        <Icon name={item.titleIcon} size="s" />
                      </Text>
                    )}
                    <Text
                      noWrap
                      variant="value"
                      elementType="span"
                      color="high_emphasis"
                    >
                      {item.title}
                    </Text>
                  </Stack>

                  {item.subtitle}
                </div>
                {item.loading && (
                  <ProgressCircle
                    size="xs"
                    aria-label="Loading subcollections"
                  />
                )}
                {!item.loading && onItemButtonPress && (
                  <Button
                    className={styles.item_action}
                    variant="secondary"
                    size="s"
                    icon={<Icon name={actionIcon} size="s" />}
                    aria-label={`Open ${item.title}`}
                    onPress={() => onItemButtonPress(item.id)}
                  />
                )}
              </div>
            );
          }}
        </TreeItemContent>
        <Collection items={childrenWithLoadMore}>
          {(child) => renderItem(child, level + 1)}
        </Collection>
      </TreeItem>
    );
  }

  const tree = (
    <RACTree
      {...aria}
      className={cx([
        styles.tree,
        isVirtualized ? styles.tree_virtualized : undefined,
        className,
      ])}
      items={items}
      defaultExpandedKeys={defaultExpandedKeys}
      expandedKeys={expandedKeys}
      onExpandedChange={onExpandedChange}
      selectionMode={selectionMode}
      selectionBehavior={selectionBehavior}
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      onAction={onAction}
      disallowEmptySelection={disallowEmptySelection}
      disabledBehavior={disabledBehavior}
      dragAndDropHooks={dragAndDropHooks}
    >
      {renderItem}
    </RACTree>
  );

  if (isVirtualized) {
    return (
      <Virtualizer layout={ListLayout} layoutOptions={{ estimatedRowSize }}>
        {tree}
      </Virtualizer>
    );
  }

  return tree;
}
